import { AnalyticsManager } from 'utils/analytics/AnalyticsManager'
import { ActionDataKey, ActionName } from 'utils/analytics/AnalyticsManager/AnalyticsManager.types'
import { CONSTANTS } from 'utils/constants'
import { getItemId, formatPriceAsFloat } from 'utils/utils'
import dependencies from 'utils/analytics/AnalyticsManager/AnalyticsManager.dependencies'

import type {
	Action,
	PurchaseAction,
	ViewItemAction,
	AddToCartAction,
	BeginCheckoutAction,
} from 'utils/analytics/AnalyticsManager/AnalyticsManager.types'

/**
 * We cannot refactor the system to use the new analytics manager as is,
 * This adapter is a temporary solution to allow us to use the new analytics manager.
 * It will apply the needed normalization in order to support It.
 */

type EnhancedEcommerceEvent = keyof typeof CONSTANTS.ANALYTICS.ENHANCED_ECOMMERCE_EVENTS

const enhancedEcommerceEvents = CONSTANTS.ANALYTICS.ENHANCED_ECOMMERCE_EVENTS

class AnalyticsAdapter {
	private analyticsManager: AnalyticsManager | null

	constructor() {
		this.analyticsManager = null
	}

	public init(tenantId: string, host: string) {
		this.analyticsManager = new AnalyticsManager(tenantId, host, dependencies)
	}

	public async sendEnhancedEcommerceEvent(eventType: EnhancedEcommerceEvent, ...args: any[]) {
		return this.sendEvent(eventType, ...args)
	}

	public async sendCustomEvent(data: any) {
		return this.sendEvent('', data)
	}

	public async sendPageViewEvent(path: string) {
		return this.sendEvent('pageView', path)
	}

	private async sendEvent(eventType: string, ...args: any[]) {
		if (!this.analyticsManager) {
			return
		}

		let actionData: Action | null = null
		let actionName: ActionName | '' = ''

		// normalize enhanced e-commerce events
		if (eventType && enhancedEcommerceEvents[eventType as EnhancedEcommerceEvent]) {
			switch (eventType) {
				case enhancedEcommerceEvents.sendPurchaseEvent: {
					const itemArray = args?.[0]
					const orderConfirmation = args?.[1]
					const metaData = args?.[2]

					const _items: string[] = []
					let numberOfItems = 0

					itemArray.forEach((_itemElement: any, _index: number) => {
						_items.push(getItemId(_itemElement))

						numberOfItems += _itemElement.quantity || 1 // the courseList from the orderConfirm API doesn't group the same items together but has 1 menu-item per element so the quantity will then always be 1
					})

					actionName = ActionName.PURCHASE
					actionData = {
						[ActionDataKey.ORDER_NUMBER]: orderConfirmation?.id,
						[ActionDataKey.TICTUK_ORDER_ID]: orderConfirmation?.tictuk_orderid,
						[ActionDataKey.SHIPPING_TIER]: orderConfirmation?.shipping_tier,
						[ActionDataKey.AFFILIATION]: orderConfirmation?.affiliation,
						[ActionDataKey.PAYMENT_TYPE]: orderConfirmation?.payment_type,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(
							orderConfirmation?.revenue,
							orderConfirmation.currency,
							orderConfirmation.countryCode,
							1,
							false,
							true
						),
						[ActionDataKey.PRODUCT_ITEM_IDS]: _items,
						[ActionDataKey.TAX]: orderConfirmation?.tax,
						[ActionDataKey.DELIVERY_CHARGE]: orderConfirmation?.shipping,
						[ActionDataKey.IS_FIRST_PURCHASE]: orderConfirmation?.isFirstPurchase,
						[ActionDataKey.CURRENCY]: metaData?.currency,
						[ActionDataKey.COUPON]: orderConfirmation?.coupon,
						[ActionDataKey.USER_EMAIL]: metaData?.user?.email,
						[ActionDataKey.USER_FIRST_NAME]: metaData?.user?.first_name,
						[ActionDataKey.USER_LAST_NAME]: metaData?.user?.last_name,
						[ActionDataKey.USER_PHONE_NUMBER]: metaData?.user?.phone_number,
						[ActionDataKey.USER_STATUS]: metaData?.user?.status,
						[ActionDataKey.NUMBER_OF_ITEMS]: numberOfItems,
					} as PurchaseAction
					break
				}
				/**
				 * we never fire the sendViewProdutDetailEvent,
				 * We use sendProductClickEvent instead.
				 * That is how we do it in all the other places.
				 * It is wrong but we are not changing it as part of this task
				 */
				case enhancedEcommerceEvents.sendProductClickEvent:
				case enhancedEcommerceEvents.sendAddToCartEvent: {
					const itemData = args?.[0]
					const eventMetaData = args?.[1]
					const restData = args?.[2]

					actionName = enhancedEcommerceEvents.sendAddToCartEvent === eventType ? ActionName.ADD_TO_CART : ActionName.VIEW_ITEM
					actionData = {
						[ActionDataKey.ITEM_ID]: getItemId(itemData),
						[ActionDataKey.CURRENCY]: eventMetaData?.currency,
						[ActionDataKey.ITEM_SECTION_TITLE]: eventMetaData?.sectionTitle,
						[ActionDataKey.REVENUE]: formatPriceAsFloat(itemData?.price, restData.currency, restData.countryCode, 1, false, true), //itemData?.price,
					} as ViewItemAction | AddToCartAction
					break
				}
				case enhancedEcommerceEvents.sendCheckOutEvent: {
					const restData = args?.[0]
					const stepData = args?.[1]
					const cartFields = args?.[2]

					if (
						stepData?.step === CONSTANTS.CHECKOUT_STEPS.GOTO_CHECKOUT_PAGE ||
						stepData?.step === CONSTANTS.CHECKOUT_STEPS.SELECTED_PAYMENT_METHOD
					) {
						const _items: string[] = []
						let numberOfItems = 0

						Object.keys(cartFields.items).forEach((_key) => {
							const _itemArray = cartFields.items[_key]
							_itemArray.forEach((_itemElement: any) => {
								const _fullItem = restData.items[_itemElement.itemId]

								_items.push(getItemId(_fullItem))
								numberOfItems += _itemElement.quantity
							})
						})

						actionName =
							CONSTANTS.CHECKOUT_STEPS.GOTO_CHECKOUT_PAGE === stepData.step ? ActionName.BEGIN_CHECKOUT : ActionName.ADD_PAYMENT_INFO

						actionData = {
							[ActionDataKey.COUNTRY_CODE]: restData?.countryCode,
							[ActionDataKey.PRODUCT_ITEM_IDS]: _items,
							[ActionDataKey.REVENUE]: formatPriceAsFloat(
								cartFields.grandTotal,
								restData.currency,
								restData.countryCode,
								1,
								false,
								true
							),
							[ActionDataKey.CURRENCY]: restData?.currency,
							[ActionDataKey.STORE_ID]: stepData?.storeID,
							[ActionDataKey.CHECKOUT_STEP]: stepData?.step,
							[ActionDataKey.STORE_NAME]: stepData?.storeName,
							[ActionDataKey.SHIPPING_TIER]: stepData?.deliveryType || '',
							[ActionDataKey.NUMBER_OF_ITEMS]: numberOfItems,
						} as BeginCheckoutAction
					}

					break
				}
				default: {
					return
				}
			}
		} else if (eventType === 'pageView') {
			// normalize pageView events
		} else {
			// normalize custom events
		}

		if (actionName && actionData) {
			return this.analyticsManager.sendEvent(actionName, actionData)
		}
	}
}

export { AnalyticsAdapter }
